/**
 * ToolCallCard Component
 *
 * Displays a single tool call with its status, parameters, metadata, and result.
 * Claude Code-inspired design with slide-in animation.
 * Supports detailed views for specific tool types and transparency metadata.
 */

import { Box, Typography, Collapse, IconButton, Button, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { ToolCall, ConfidenceCriterion } from '../../contexts/chat/type';
import { AtRiskSkusTable } from './at_risk_skus_table';
import { ContributingFactorsDisplay } from './contributing_factors_display';

interface ToolCallCardProps {
  toolCall: ToolCall;
  isStreaming?: boolean;
  defaultExpanded?: boolean;
}

// Quality level colors
const QUALITY_COLORS: Record<string, string> = {
  good: '#10b981',
  moderate: '#f59e0b',
  limited: '#f97316',
  poor: '#ef4444',
  none: '#9ca3af',
};

// Confidence level colors
const CONFIDENCE_COLORS: Record<string, string> = {
  high: '#10b981',
  medium: '#f59e0b',
  low: '#ef4444',
};

// Animated spinner component
const SpinnerIcon = () => (
  <Box
    sx={{
      width: 16,
      height: 16,
      border: '2px solid #e5e7eb',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      '@keyframes spin': {
        to: { transform: 'rotate(360deg)' },
      },
    }}
  />
);

// Check if tool has detailed view available
const hasDetailedView = (toolName: string, result: unknown): boolean => {
  if (!result || typeof result !== 'object') return false;
  const raw = (result as { raw?: unknown }).raw;
  if (!raw || typeof raw !== 'object') return false;

  switch (toolName) {
    case 'find_at_risk_skus':
      return Array.isArray((raw as { at_risk_skus?: unknown }).at_risk_skus);
    case 'diagnose_shortfall':
      // contributing_factors is nested inside root_cause
      const rootCause = (raw as { root_cause?: { contributing_factors?: unknown } }).root_cause;
      return Array.isArray(rootCause?.contributing_factors);
    default:
      return false;
  }
};

// Confidence criteria display component
const ConfidenceCriteria = ({ criteria }: { criteria: ConfidenceCriterion[] }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
    {criteria.map((criterion, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {criterion.met ? (
          <CheckIcon sx={{ fontSize: 14, color: '#10b981' }} />
        ) : (
          <CloseIcon sx={{ fontSize: 14, color: '#d1d5db' }} />
        )}
        <Typography sx={{ fontSize: '11px', color: criterion.met ? '#374151' : '#9ca3af' }}>
          {criterion.label}
          {criterion.detail && (
            <span style={{ color: '#6b7280', marginLeft: 4 }}>({criterion.detail})</span>
          )}
        </Typography>
      </Box>
    ))}
  </Box>
);

// Metadata info line component
const MetadataLine = ({ icon, children }: { icon: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
    <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
      {icon} {children}
    </Typography>
  </Box>
);

export const ToolCallCard = ({
  toolCall,
  isStreaming = false,
  defaultExpanded = true
}: ToolCallCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Details expanded by default during streaming, collapsed after
  const [showDetails, setShowDetails] = useState(isStreaming);
  const dm = toolCall.displayMetadata;

  // Update showDetails when streaming state changes
  useEffect(() => {
    if (!isStreaming && toolCall.status === 'success') {
      // Collapse details after streaming completes
      setShowDetails(false);
    }
  }, [isStreaming, toolCall.status]);

  const statusIcon = {
    pending: <SpinnerIcon />,
    running: <SpinnerIcon />,
    success: <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />,
    error: <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />,
  }[toolCall.status];

  const displayLabel = toolCall.status === 'running' || toolCall.status === 'pending'
    ? toolCall.activeDisplayName
    : toolCall.displayName;

  // Format parameters for display
  const paramEntries = Object.entries(toolCall.parameters).filter(
    ([_, value]) => value !== undefined && value !== null && value !== ''
  );

  // Check if this tool has detailed data to show
  const canShowDetails = toolCall.status === 'success' &&
    (hasDetailedView(toolCall.toolName, toolCall.result) || dm?.confidence || dm?.comparison);

  // Extract detailed data from result
  const getAtRiskSkus = () => {
    const raw = toolCall.result?.raw as { at_risk_skus?: unknown } | undefined;
    return raw?.at_risk_skus as Array<{
      sku: string;
      location: string;
      risk_type: 'stockout' | 'below_target';
      days_until_risk: number;
      total_unfulfilled_demand: number;
    }> | undefined;
  };

  const getContributingFactors = () => {
    // contributing_factors is nested inside root_cause
    const raw = toolCall.result?.raw as { root_cause?: { contributing_factors?: unknown } } | undefined;
    return raw?.root_cause?.contributing_factors as Array<{
      variable: string;
      change_pct: number;
    }> | undefined;
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        overflow: 'hidden',
        mb: 1,
        animation: 'slideIn 0.2s ease-out',
        '@keyframes slideIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#f1f3f4' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {statusIcon}
          <Typography
            sx={{
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '13px',
              fontWeight: 600,
              color: '#1a1a1a',
            }}
          >
            {displayLabel}
          </Typography>
          {toolCall.duration !== undefined && toolCall.duration > 0 && toolCall.status === 'success' && (
            <Typography
              sx={{
                fontSize: '11px',
                color: '#9ca3af',
                ml: 1,
              }}
            >
              {(toolCall.duration / 1000).toFixed(1)}s
            </Typography>
          )}
        </Box>
        <IconButton size="small" sx={{ p: 0.5 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Parameters */}
        {paramEntries.length > 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography
              component="div"
              sx={{
                fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                fontSize: '12px',
                color: '#6b7280',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              {paramEntries.map(([key, value], i) => (
                <span key={key}>
                  <span style={{ color: '#9333ea' }}>{key}</span>
                  <span style={{ color: '#6b7280' }}>=</span>
                  <span style={{ color: '#059669' }}>"{String(value)}"</span>
                </span>
              ))}
            </Typography>
          </Box>
        )}

        {/* Divider */}
        {(dm || toolCall.metadata.runTimestamp || toolCall.result) && (
          <Box sx={{ borderTop: '1px dashed #e5e7eb', mx: 2 }} />
        )}

        {/* Metadata & Result */}
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* Display Metadata - Transparency UI */}
          {dm && (
            <Box sx={{ mb: 1 }}>
              {/* Data source line */}
              <MetadataLine icon="📊">
                {dm.data_source.name}
                {dm.data_source.row_count_display && (
                  <> · {dm.data_source.row_count_display}</>
                )}
                {dm.data_source.date_range_display && (
                  <> · {dm.data_source.date_range_display}</>
                )}
              </MetadataLine>

              {/* Run date line */}
              {dm.run.date_display && (
                <MetadataLine icon="📅">
                  Run from {dm.run.date_display}
                </MetadataLine>
              )}

              {/* Reference dates (for at-risk analysis) */}
              {dm.reference_dates && (
                <MetadataLine icon="📆">
                  {dm.reference_dates.horizon_display}
                  {dm.reference_dates.today_proxy_display && (
                    <> · Starting {dm.reference_dates.today_proxy_display}</>
                  )}
                </MetadataLine>
              )}

              {/* Comparison method (for diagnosis) */}
              {dm.comparison && (
                <MetadataLine icon="🔄">
                  {dm.comparison.method_display}
                  {dm.comparison.previous_run_display && (
                    <> ({dm.comparison.previous_run_display})</>
                  )}
                </MetadataLine>
              )}

              {/* Confidence level (always visible for diagnosis) */}
              {dm.confidence && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={dm.confidence.display}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: `${CONFIDENCE_COLORS[dm.confidence.level]}15`,
                      color: CONFIDENCE_COLORS[dm.confidence.level],
                      border: `1px solid ${CONFIDENCE_COLORS[dm.confidence.level]}40`,
                    }}
                  />
                  {dm.comparison && dm.comparison.quality_level !== 'none' && (
                    <Chip
                      label={dm.comparison.quality_display}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor: `${QUALITY_COLORS[dm.comparison.quality_level]}15`,
                        color: QUALITY_COLORS[dm.comparison.quality_level],
                        border: `1px solid ${QUALITY_COLORS[dm.comparison.quality_level]}40`,
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Legacy metadata fallback */}
          {!dm && toolCall.metadata.runTimestamp && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                {'\uD83D\uDCCA'} Run #{toolCall.metadata.runId?.slice(-4) ?? '??'} {'\u00B7'} {
                  new Date(toolCall.metadata.runTimestamp).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                }
              </Typography>
            </Box>
          )}

          {/* Result summary */}
          {toolCall.result && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                sx={{
                  fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                  fontSize: '12px',
                  color: toolCall.error ? '#ef4444' : '#374151',
                }}
              >
                {'\u2192'} {toolCall.result.summary}
              </Typography>

              {/* View details toggle */}
              {canShowDetails && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDetails(!showDetails);
                  }}
                  startIcon={showDetails ? <VisibilityOffIcon sx={{ fontSize: 14 }} /> : <VisibilityIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '11px',
                    color: '#6b7280',
                    minWidth: 'auto',
                    px: 1,
                    py: 0.25,
                    '&:hover': { backgroundColor: '#e5e7eb' },
                  }}
                >
                  {showDetails ? 'Hide details' : 'View details'}
                </Button>
              )}
            </Box>
          )}

          {/* Detailed View Section */}
          <Collapse in={showDetails}>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e5e7eb' }}>
              {/* Confidence criteria (for diagnosis) */}
              {dm?.confidence?.criteria && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 0.5,
                    }}
                  >
                    Confidence Criteria
                  </Typography>
                  <ConfidenceCriteria criteria={dm.confidence.criteria} />
                </Box>
              )}

              {/* Comparison details (for diagnosis) */}
              {dm?.comparison && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 0.5,
                    }}
                  >
                    Run Selection
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#374151' }}>
                    {dm.comparison.search_summary}
                  </Typography>
                  {dm.comparison.overlap_days_display && (
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.25 }}>
                      {dm.comparison.overlap_days_display}
                    </Typography>
                  )}
                </Box>
              )}

              {/* At-Risk SKUs Table */}
              {toolCall.toolName === 'find_at_risk_skus' && getAtRiskSkus() && (
                <AtRiskSkusTable skus={getAtRiskSkus()!} />
              )}

              {/* Contributing Factors Display */}
              {toolCall.toolName === 'diagnose_shortfall' && getContributingFactors() && (
                <ContributingFactorsDisplay factors={getContributingFactors()!} />
              )}
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </Box>
  );
};
